import { EventEmitter } from '@angular/core';
import { Headers } from '@angular/http';
import { Http } from '@angular/http';
import { Injectable } from '@angular/core';
import { RequestMethod } from '@angular/http';
import { RequestOptionsArgs } from '@angular/http';
import { Response } from '@angular/http';
import { ResponseOptions } from '@angular/http';
import { URLSearchParams } from '@angular/http';

import { throwError, Observable, Subscription, of } from 'rxjs';

import { camelCaseObject, snakeCase } from './case-transform';
import { LocalStorageService } from 'app/services/storage/local-storage.service';
import { PostMessageService } from 'app/services/post-message.service';
import { catchError, flatMap, map, tap } from 'rxjs/operators';

interface OAuth {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
  created?: number;
}

const CLIENT_BASIC =
  'Basic NTRiYzEzM2E4ZTVmMmVkZTAwYzcwNTY5OmMwOGpObXhZYkZwUE5GSmFmV3hVY1hWdFduST0=';

const FORM_CONTENT_TYPE = 'application/x-www-form-urlencoded';

const WEB_API_URL = '/web-api';

const OAUTH_URL = `${WEB_API_URL}/v0/token`;

const LOCKED_KEY = 'locked';

const OAUTH_KEY = 'oauth';

const SIXTY_SECONDS = 60000;

function camelCaseResponse(response: Response) {
  try {
    return camelCaseObject(response.json());
  } catch (e) {
    return response.text();
  }
}

function camelCaseError(response: Response): Response {
  return new Response(
    new ResponseOptions({
      body: camelCaseResponse(response),
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      type: response.type,
      url: response.url
    })
  );
}

function mergeRequestOptions(
  body?: any,
  options?: RequestOptionsArgs
): RequestOptionsArgs {
  return Object.assign({ body }, options || {});
}

const UID = (() => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      // tslint:disable-next-line:no-bitwise
      const r = Math.random() * 16 | 0,
        // tslint:disable-next-line:no-bitwise
        v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
})()

@Injectable()
export class WebApi {
  private _oauth: OAuth;

  events = new EventEmitter<string>();

  /** Возвращает url запроса к web-api */
  static url(url: string): string {
    return `${WEB_API_URL}${url}`;
  }

  constructor(
    private _http: Http,
    private _localStorageService: LocalStorageService,
    private _postMessageService: PostMessageService
  ) {
    this._oauth = this._localStorageService.getJson(OAUTH_KEY);

    if (this.constructor.name === 'WebApi') {
      // от WebApi наследуется Api и поэтому раньше было дублирование вызовов
      this._unlockHttpQueue();
      setInterval(() => this._prolongSession(), SIXTY_SECONDS);
      window.addEventListener('pagehide', () => {
        if (this._isMyLockHttpQueue()) {
          this._unlockHttpQueue();
        }
        return '';
      }, {capture: true});
    }

    this._localStorageService.changeKey.subscribe(data => {
      if (data.key === OAUTH_KEY) {
        this._oauth = this._localStorageService.getJson(OAUTH_KEY);
        if (!this._oauth) {
          this.events.emit('logout');
        }
      }
    });
  }

  /** Выполняет get запрос */
  get(url: string, options?: RequestOptionsArgs): Observable<any> {
    return this._request(url, RequestMethod.Get, options);
  }

  /** Выполняет post запрос */
  post(url: string, body?: any, options?: RequestOptionsArgs): Observable<any> {
    return this._request(
      url,
      RequestMethod.Post,
      mergeRequestOptions(body, options)
    );
  }

  /** Выполняет put запрос */
  put(url: string, body?: any, options?: RequestOptionsArgs): Observable<any> {
    return this._request(
      url,
      RequestMethod.Put,
      mergeRequestOptions(body, options)
    );
  }

  /** Выполняет delete запрос */
  delete(url: string, options?: RequestOptionsArgs): Observable<any> {
    return this._request(url, RequestMethod.Delete, options);
  }

  getOnce(url: string, options?: RequestOptionsArgs): Observable<any> {
    return this._requestOnce(url, RequestMethod.Get, options);
  }

  private _requestOnce(
    url: string,
    method: RequestMethod,
    options: RequestOptionsArgs
  ): Observable<any> {
    options = this._createRequestOptions(url, method, options);
    return this._http.request(options.url, options).pipe(
      map(response => camelCaseResponse(response)),
      catchError(response => throwError(camelCaseError(response)))
    );
  }

  private _request(
    url: string,
    method: RequestMethod,
    options: RequestOptionsArgs
  ): Observable<any> {
    return this._waitUnlockHttpQueue().pipe(
      flatMap(() => {
        options = this._createRequestOptions(url, method, options);
        return this._http.request(options.url, options);
      }),
      map(response => {
        return camelCaseResponse(response);
      }),
      catchError(response => {
        if (response.status === 401) {
          const authHeader = this.getAuthorizationHeader();
          if (
            options.headers.get('Authorization') !== authHeader ||
            this._isLockedHttpQueue()
          ) {
            options.headers.delete('Authorization');
            return this._waitUnlockHttpQueue().pipe(
              flatMap(() => this._requestOnce(url, method, options))
            );
          }
          return this._restoreSessionWithLock().pipe(
            flatMap(() => {
              options.headers.delete('Authorization');
              return this._requestOnce(url, method, options);
            })
          );
        }

        return throwError(camelCaseError(response));
      })
    );
  }

  login(email: string, password: string): Observable<void> {
    const params = new URLSearchParams();
    params.append('grant_type', 'password');
    params.append('username', email);
    params.append('password', password);
    return this._oauthRequest(params).pipe(
      tap(() => this.events.emit('login'))
    );
  }

  logout(): Observable<void> {
    this._localStorageService.remove(OAUTH_KEY);
    this.events.emit('logout');
    return of(null);
  }

  register(user): Observable<void> {
    const params = new URLSearchParams();
    params.append('grant_type', 'sign_up');
    for (const key of Object.keys(user)) {
      params.append(snakeCase(key), user[key]);
    }
    return this._oauthRequest(params).pipe(
      tap(() => this.events.emit('register'))
    );
  }

  recoverPassword(email: string, resetPasswordUrl: string): Observable<void> {
    const params = new URLSearchParams();
    params.append('grant_type', 'client_credentials');

    return this._oauthRequest(params).pipe(
      flatMap(() =>
        this.post('/v0/user/password/request', {
          email: email,
          reset_password_url: resetPasswordUrl
        })
      )
    );
  }

  changePassword(password: string, token: string): Observable<void> {
    const params = new URLSearchParams();
    params.append('grant_type', 'client_credentials');

    return this._oauthRequest(params).pipe(
      flatMap(() =>
        this.post('/v0/user/password/commit', {
          password: password,
          token: token
        })
      )
    );
  }

  private _prolongSession(): void {
    if (this._oauth && !this._isLockedHttpQueue()) {
      const { accessToken, created, expiresIn } = this._oauth;
      const expires = created + (expiresIn - 3 * SIXTY_SECONDS);
      if (expires < Date.now()) {
        if (accessToken !== this._oauth.accessToken) {
          return;
        }
        this._restoreSessionWithLock().subscribe();
      }
    }
  }

  private _oauthRequest(params: URLSearchParams): Observable<void> {
    const headers = new Headers({
      Authorization: CLIENT_BASIC,
      'Content-Type': FORM_CONTENT_TYPE
    });
    return this._http.post(OAUTH_URL, params.toString(), { headers }).pipe(
      map(response => camelCaseResponse(response)),
      map(oauth => this._setOAuth(oauth)),
      catchError(response => throwError(camelCaseError(response)))
    );
  }

  private _setOAuth(response: OAuth): void {
    this._oauth = {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      tokenType: response.tokenType,
      expiresIn: 1000 * response.expiresIn,
      created: Date.now()
    };
    this._localStorageService.setJson(OAUTH_KEY, this._oauth);
  }

  protected _getUrl(url: string) {
    return WebApi.url(url);
  }

  private _createRequestOptions(
    url: string,
    method: RequestMethod,
    options: RequestOptionsArgs
  ): RequestOptionsArgs {
    options = options || {};
    this._setAuthorizationHeader(options);
    options.method = method;
    options.url = this._getUrl(url);
    return options;
  }

  private _setAuthorizationHeader(
    options: RequestOptionsArgs
  ): RequestOptionsArgs {
    const headers = options.headers || new Headers();
    if (!headers.has('Authorization')) {
      headers.set('Authorization', this.getAuthorizationHeader());
    }
    options.headers = headers;
    return options;
  }

  public getAuthorizationHeader(): string {
    return `Bearer ${this._oauth && this._oauth.accessToken}`;
  }

  private _restoreSessionWithLock(): Observable<void> {
    if (this._isLockedHttpQueue()) {
      return this._waitUnlockHttpQueue();
    }
    this._lockHttpQueue();
    return Observable.create(observer => {
      setTimeout(() => {
        if (!this._isMyLockHttpQueue()) {
          return this._waitUnlockHttpQueue().subscribe(() => {
            observer.next();
            observer.complete();
          });
        }
        this._restoreSession().subscribe(() => {
          observer.next();
          observer.complete();
        });
      }, 100);
    });
  }

  private _restoreSession(): Observable<void> {
    // несмотря на то, что есть changeKey для OAUTH_KEY,
    // иногда сюда попадает раньше чем срабатывает changeKey
    this._oauth = this._localStorageService.getJson(OAUTH_KEY);

    if (this._oauth && this._oauth.refreshToken) {
      const params = new URLSearchParams();
      params.append('grant_type', 'refresh_token');
      params.append('refresh_token', this._oauth.refreshToken);

      return this._oauthRequest(params).pipe(
        tap(() => {
          this._unlockHttpQueue();
          this.events.emit('restore');
        }),
        catchError(error => {
          this._unlockHttpQueue();
          this._localStorageService.remove(OAUTH_KEY);
          return throwError(error);
        })
      );
    } else {
      return Observable.create(observer => {
        const postMessageRefresh = (message: {
          accessToken: string;
          action: string;
        }) => {
          this._unlockHttpQueue();
          if (message && message.action === 'accessTokenUpdated') {
            this._postMessageService.off(postMessageRefresh);
            this._setOAuth(<OAuth>{ accessToken: message.accessToken });
            observer.next();
            observer.complete();
            this.events.emit('restore');
          }
        };
        this._postMessageService.on(postMessageRefresh);
        PostMessageService.sendTo(
          window.parent,
          JSON.stringify({ action: 'accessTokenExpired' })
        );
      });
    }
  }

  private _isLockedHttpQueue(): boolean {
    return !!this._localStorageService.get(LOCKED_KEY);
  }

  private _lockHttpQueue(): void {
    this._localStorageService.set(LOCKED_KEY, UID);
  }

  private _isMyLockHttpQueue(): boolean {
    return this._localStorageService.get(LOCKED_KEY) === UID;
  }

  private _unlockHttpQueue(): void {
    this._localStorageService.remove(LOCKED_KEY);
  }

  private _waitUnlockHttpQueue(): Observable<void> {
    if (this._isLockedHttpQueue()) {
      return Observable.create(observer => {
        const unlockSubscription: Subscription = this._localStorageService.changeKey.subscribe(
          data => {
            if (data.key === LOCKED_KEY && !data.value) {
              unlockSubscription.unsubscribe();
              observer.next(void 0);
              observer.complete();
            }
          }
        );
      });
    }
    return of(null);
  }

  setAuthToken(xhr: XMLHttpRequest): void {
    xhr.setRequestHeader('Authorization', this.getAuthorizationHeader());
  }
}
